import React, { useState, useEffect, useCallback } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import OrbitTreeD3 from '../components/OrbitTreeD3';
import FullPotentialOverlay from '../components/FullPotentialOverlay';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { Planet, ListBullets, ArrowLeft } from '@phosphor-icons/react';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/orbit`;

const rankColorMap = {
  mercure: '#9CA3AF', venus: '#FBBF24', terre: '#60A5FA',
  mars: '#F87171', jupiter: '#FB923C', saturne: '#A78BFA'
};

const OrbitMatrixPage = () => {
  const { language } = useLanguage();
  const { user } = useOrbitAuth();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('orbit');
  const [depth, setDepth] = useState(5);
  const [rootId, setRootId] = useState(null);
  const [rootHistory, setRootHistory] = useState([]);
  const [showFullPotential, setShowFullPotential] = useState(false);

  const t = (fr, en) => language === 'fr' ? fr : en;

  const fetchTree = useCallback(async (targetRootId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('orbit-token');
      const params = `depth=${depth}${targetRootId ? `&root_id=${targetRootId}` : ''}`;
      const res = await axios.get(`${API}/matrix/tree?${params}`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setTree(res.data.tree);
    } catch { toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error'); }
    finally { setLoading(false); }
  }, [depth, language]);

  useEffect(() => { fetchTree(rootId); }, [depth, rootId, fetchTree]);

  const handleDrillDown = useCallback((userId) => {
    if (userId === tree?.user_id) return;
    setRootHistory(prev => [...prev, rootId]);
    setRootId(userId);
  }, [tree, rootId]);

  const handleGoBack = useCallback(() => {
    if (rootHistory.length === 0) {
      setRootId(null);
      return;
    }
    const prev = rootHistory[rootHistory.length - 1];
    setRootHistory(h => h.slice(0, -1));
    setRootId(prev);
  }, [rootHistory]);

  const renderListView = (node, level = 0) => {
    if (!node) return null;
    const color = rankColorMap[node.rank] || '#9CA3AF';
    return (
      <div key={node.user_id} style={{ marginLeft: level * 20 }} className="py-1">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#111827]/80 border border-indigo-500/10 rounded-lg hover:border-indigo-500/30 transition-colors">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: color }}>
            {node.name?.charAt(0) || '?'}
          </div>
          <span className="text-white text-sm font-medium truncate flex-1">{node.name}</span>
          <span className="text-xs px-1.5 py-0.5 rounded capitalize" style={{ color, backgroundColor: color + '20' }}>{node.rank}</span>
          <span className="text-xs text-gray-500">{t('Orbite', 'Orbit')} {node.orbit}</span>
          <span className="text-xs text-gray-400">{node.total_downline} {t('en reseau', 'downline')}</span>
        </div>
        {(node.children || []).map(child => renderListView(child, level + 1))}
      </div>
    );
  };

  return (
    <OrbitLayout>
      <div className="space-y-4" data-testid="orbit-matrix-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              {rootId && (
                <button onClick={handleGoBack}
                  className="p-1.5 bg-[var(--ls-terracotta)]/10 rounded-lg text-[var(--ls-terracotta)] hover:bg-[var(--ls-terracotta)]/20 transition-colors mr-1"
                  data-testid="orbit-back-btn">
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              {t('Mon Orbite', 'My Orbit')}
            </h1>
            <p className="text-gray-300 text-sm mt-1">
              {t('Matrice 4 directions × 7 niveaux', '4 directions × 7 levels matrix')} — {tree?.total_downline || 0} {t('membres', 'members')}
              {rootId && <span className="text-[var(--ls-terracotta)] ml-2">({t('vue depuis', 'viewing from')} {tree?.name})</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFullPotential(true)}
              className="group flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[var(--ls-terracotta)] to-amber-500 text-white text-sm font-semibold shadow-lg shadow-[var(--ls-terracotta)]/25 hover:shadow-[var(--ls-terracotta)]/40 hover:scale-[1.03] active:scale-[0.98] transition-all"
              data-testid="full-potential-btn">
              <Sparkles className="w-4 h-4 group-hover:animate-spin" />
              {t('Potentiel Max', 'Full Potential')}
            </button>
            <div className="flex bg-white border border-[hsl(40,20%,88%)] rounded-lg overflow-hidden text-sm">
              <button onClick={() => setView('orbit')}
                className={`flex items-center gap-1.5 px-3 py-2 transition-colors ${view === 'orbit' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-[var(--ls-text-muted)] hover:text-[var(--ls-text)]'}`}
                data-testid="orbit-view-orbit">
                <Planet className="w-4 h-4" /> {t('Orbite', 'Orbit')}
              </button>
              <button onClick={() => setView('list')}
                className={`flex items-center gap-1.5 px-3 py-2 transition-colors ${view === 'list' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-gray-400 hover:text-white'}`}
                data-testid="orbit-view-list">
                <ListBullets className="w-4 h-4" /> {t('Liste', 'List')}
              </button>
            </div>
            <select value={depth} onChange={e => setDepth(Number(e.target.value))}
              className="bg-white border border-[hsl(40,20%,88%)] rounded-lg text-[var(--ls-text)] text-sm px-3 py-2"
              data-testid="orbit-depth-select">
              {[3, 4, 5, 6, 7].map(d => (
                <option key={d} value={d}>{d} {t('niveaux', 'levels')}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Orbit View */}
        {view === 'orbit' && !loading && (
          <div className="border border-indigo-500/10 rounded-xl overflow-hidden" style={{ height: 520 }}>
            <OrbitTreeD3
              tree={tree}
              matrixWidth={4}
              systemType="4x7"
              onDrillDown={handleDrillDown}
              language={language}
              maxDepth={depth}
            />
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center h-[520px] bg-[#0B0E17] border border-indigo-500/10 rounded-xl">
            <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* List View */}
        {view === 'list' && !loading && (
          <div className="bg-[#0B0E17] border border-indigo-500/10 rounded-xl p-4 max-h-[520px] overflow-y-auto">
            {tree ? renderListView(tree) : (
              <p className="text-gray-500 text-center py-8">{t('Aucune donnee', 'No data')}</p>
            )}
          </div>
        )}

        {/* Legend + Stats */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {Object.entries(rankColorMap).map(([rank, color]) => (
              <div key={rank} className="flex items-center gap-1.5 text-xs text-[var(--ls-text-muted)]">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="capitalize">{rank}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[var(--ls-text-muted)]">{t('Cliquez sur un noeud pour explorer • Double-clic pour recentrer', 'Click a node to explore • Double-click to recenter')}</p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: t('Position', 'Position'), value: user?.position || '-' },
            { label: t('Orbite', 'Orbit'), value: user?.orbit_level || 0 },
            { label: t('Enfants directs', 'Direct Children'), value: tree?.children_count || 0 },
            { label: t('Reseau total', 'Total Network'), value: tree?.total_downline || 0 },
          ].map((item, i) => (
            <div key={i} className="bg-white border border-[hsl(40,20%,88%)] rounded-lg p-3 text-center">
              <p className="text-xs text-[var(--ls-text-muted)]">{item.label}</p>
              <p className="text-lg font-bold text-[var(--ls-text)] mt-1">{typeof item.value === 'number' ? item.value.toLocaleString() : item.value}</p>
            </div>
          ))}
        </div>

        <FullPotentialOverlay
          isOpen={showFullPotential}
          onClose={() => setShowFullPotential(false)}
          language={language}
        />
      </div>
    </OrbitLayout>
  );
};

export default OrbitMatrixPage;
